import { createFeature, createReducer, createSelector, on } from '@ngrx/store';

import { loadFromStorage } from '../../../state/persist-state.utils';

import { NavigatorOutboxActions } from './navigator-outbox.actions';
import { isCrewSave, NavigatorWrite, QueuedNavigatorWrite, withoutKeysWrittenBy } from './navigator-outbox.util';

/** How many of an event's directly stored writes are remembered for trimming a write queued late. */
const STORED_WRITES_KEPT = 5;

export interface NavigatorOutboxState {
  /** Oldest first: the order they are sent in. */
  queue: QueuedNavigatorWrite[];
  /**
   * The newest ops revision a reply has carried, per event (#86b). Kept here as well as on the
   * loaded event because live updates can be missed: a device that only learnt the revision from
   * them would have every later save dropped after one missed ops move.
   */
  knownOpsRevision: Record<string, number>;
  /**
   * The newest writes this session stored directly, per event. Writes leave the device
   * concurrently, so an older one can fail for lack of signal after a newer one has landed; it is
   * queued only then, and must lose what the newer one wrote just as a write already waiting does.
   * Not persisted: a write in flight when the app closes is not queued at all.
   */
  storedThisSession: Record<string, { seq: number; write: NavigatorWrite }[]>;
}

export const navigatorOutboxInitialState: NavigatorOutboxState = {
  // Survives a reload and a phone restart: waiting saves are the crew's own work.
  queue: loadFromStorage('navigatorOutbox', []),
  knownOpsRevision: {},
  storedThisSession: {},
};

export const navigatorOutboxFeature = createFeature({
  name: 'navigatorOutbox',
  reducer: createReducer(
    navigatorOutboxInitialState,
    on(NavigatorOutboxActions.saveQueued, (state, { entry }): NavigatorOutboxState => {
      let write: NavigatorWrite | null = entry.write;
      for (const stored of state.storedThisSession[entry.write.eventId] ?? []) {
        if (write && stored.seq > entry.seq) { write = withoutKeysWrittenBy(write, stored.write); }
      }
      return write ? { ...state, queue: [ ...state.queue, { ...entry, write } ] } : state;
    }),
    on(NavigatorOutboxActions.directSaveStored, (state, { seq, write }): NavigatorOutboxState => {
      const queue: QueuedNavigatorWrite[] = [];
      for (const entry of state.queue) {
        if (entry.write.eventId !== write.eventId || entry.seq > seq) {
          queue.push(entry);
          continue;
        }
        const remaining = withoutKeysWrittenBy(entry.write, write);
        if (remaining) { queue.push({ ...entry, write: remaining }); }
      }
      const stored = [ ...(state.storedThisSession[write.eventId] ?? []), { seq, write } ].slice(-STORED_WRITES_KEPT);
      return { ...state, queue, storedThisSession: { ...state.storedThisSession, [write.eventId]: stored } };
    }),
    on(
      NavigatorOutboxActions.replaySaveSuccess,
      NavigatorOutboxActions.replaySaveRefused,
      (state, { id }): NavigatorOutboxState => ({
        ...state,
        queue: state.queue.filter((entry) => entry.id !== id),
      }),
    ),
    on(NavigatorOutboxActions.opsRevisionSeen, (state, { eventId, opsRevision }): NavigatorOutboxState => {
      if ((state.knownOpsRevision[eventId] ?? -1) >= opsRevision) { return state; }
      return { ...state, knownOpsRevision: { ...state.knownOpsRevision, [eventId]: opsRevision } };
    }),
  ),
  extraSelectors: ({ selectQueue }) => ({
    selectCrewSavesWaiting: createSelector(selectQueue, (queue) => queue.filter(isCrewSave).length),
  }),
});
